// useMasteryJourney — tracks the visitor's home-page learning journey
// across playbook runs, even before they sign up.
//
// What we record (localStorage, anon-friendly):
//   • completedSlugs — playbooks where the user finished the LAST step
//   • launchedSkills — labels from the AI Mastery Path strip they tapped
//   • score          — simple +XP-style counter for momentum (not money)
//   • currentTier    — derived from how many playbooks they've finished
//
// Why this lives here (not in usePlaybookProgress): that hook stores the
// active session's resume payload (one slug at a time). This hook is the
// "lifetime" tally that drives the persistent progress HUD and powers
// the "next harder challenge" chain inside the runner.
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hl.mastery.journey.v1";

export type MasteryJourney = {
  completedSlugs: string[];
  launchedSkills: string[];
  score: number;
  updatedAt: number;
};

const EMPTY: MasteryJourney = {
  completedSlugs: [],
  launchedSkills: [],
  score: 0,
  updatedAt: 0,
};

function read(): MasteryJourney {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as MasteryJourney;
    return {
      completedSlugs: Array.isArray(parsed.completedSlugs) ? parsed.completedSlugs : [],
      launchedSkills: Array.isArray(parsed.launchedSkills) ? parsed.launchedSkills : [],
      score: typeof parsed.score === "number" ? parsed.score : 0,
      updatedAt: typeof parsed.updatedAt === "number" ? parsed.updatedAt : 0,
    };
  } catch {
    return EMPTY;
  }
}

function write(j: MasteryJourney) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(j));
  } catch {
    /* quota / privacy mode — silently no-op */
  }
}

/** Tier name the user is currently in, based on completed playbooks. */
export function tierFromCompleted(count: number): {
  name: "Awareness" | "Application" | "Leverage" | "Systems" | "Architecture";
  next: string | null;
  toNext: number;
} {
  // Loose mapping — every 2 finished playbooks bumps you up a tier.
  // Caps at Architecture so the highest tier is reachable without
  // requiring all 10 playbooks.
  if (count >= 8) return { name: "Architecture", next: null, toNext: 0 };
  if (count >= 6) return { name: "Systems", next: "Architecture", toNext: 8 - count };
  if (count >= 4) return { name: "Leverage", next: "Systems", toNext: 6 - count };
  if (count >= 2) return { name: "Application", next: "Leverage", toNext: 4 - count };
  return { name: "Awareness", next: "Application", toNext: 2 - count };
}

export function useMasteryJourney() {
  const [journey, setJourney] = useState<MasteryJourney>(() => read());

  // Keep tabs in sync — if the user finishes a playbook in another tab,
  // the HUD here should reflect it without a refresh.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setJourney(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const persist = useCallback((next: MasteryJourney) => {
    const row = { ...next, updatedAt: Date.now() };
    write(row);
    setJourney(row);
  }, []);

  const markPlaybookComplete = useCallback(
    (slug: string, xp = 100) => {
      setJourney((prev) => {
        if (prev.completedSlugs.includes(slug)) return prev;
        const next: MasteryJourney = {
          ...prev,
          completedSlugs: [...prev.completedSlugs, slug],
          score: prev.score + xp,
          updatedAt: Date.now(),
        };
        write(next);
        return next;
      });
    },
    [],
  );

  const recordSkillLaunch = useCallback((label: string) => {
    setJourney((prev) => {
      if (prev.launchedSkills.includes(label)) return prev;
      const next: MasteryJourney = {
        ...prev,
        launchedSkills: [...prev.launchedSkills, label],
        score: prev.score + 10,
        updatedAt: Date.now(),
      };
      write(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => persist(EMPTY), [persist]);

  const tier = tierFromCompleted(journey.completedSlugs.length);

  return {
    journey,
    tier,
    markPlaybookComplete,
    recordSkillLaunch,
    reset,
  };
}